import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import CertificateAssetUploadSlot from "../../CertificateAssetUploadSlot/CertificateAssetUploadSlot";
import EntityConstants from "../../../configs/EntityConstants";
import { POSTFORMDATA } from "../../../service/CRUDService";
import Toast from "../../Toast/Toast";
import { validateCertificateSvgFile } from "../utils/svgUploadValidation";

function isSuccessfulApiResponse(response, data) {
  if (!response?.ok) return false;
  if (data?.status_code != null && Number(data.status_code) !== 200) {
    return false;
  }
  return true;
}

function getApiErrorMessage(data, fallback) {
  return data?.message || data?.error || fallback;
}

function getSvgValidationMessage(t, validationError) {
  if (validationError === "invalid_preview_svg") {
    return t("certificateOnboarding:invalid_preview_svg");
  }
  if (validationError === "invalid_template_svg") {
    return t("certificateOnboarding:invalid_template_svg");
  }
  return t("certificateOnboarding:invalid_svg_file");
}

const SLOT_KEYS = {
  previewSvg: "previewSvg",
  templateSvg: "templateSvg",
};

function CertificateUploadStep({
  assets,
  setAssets,
  uploadingSlots,
  setUploadingSlots,
  isEntityNameValidated = false,
}) {
  const { t } = useTranslation();

  const uploadFile = useCallback(
    async (slotKey, file) => {
      try {
        const svgValidationError = await validateCertificateSvgFile(
          file,
          slotKey,
        );

        if (svgValidationError) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: getSvgValidationMessage(t, svgValidationError),
          });
          return;
        }

        setUploadingSlots((prev) => ({ ...prev, [slotKey]: true }));

        const formData = new FormData();
        formData.append("file", file);

        const response = await POSTFORMDATA(
          EntityConstants.CERTIFICATE_ONBOARDING_UPLOAD,
          formData,
        );

        const data = await response.json();

        if (!isSuccessfulApiResponse(response, data)) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: getApiErrorMessage(
              data,
              t("certificateOnboarding:upload_failed"),
            ),
          });
          return;
        }

        const uploaded = data.data;
        if (!uploaded?.url) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: t("certificateOnboarding:upload_failed"),
          });
          return;
        }

        setAssets((prev) => ({
          ...prev,
          [slotKey]: {
            file_name: uploaded.file_name,
            file_url: uploaded.file_url,
            url: uploaded.url,
          },
        }));

        Toast({
          type: "success",
          title: t("certificateOnboarding:success"),
          message: data.message || t("certificateOnboarding:upload_success"),
        });
      } catch (error) {
        console.error("Certificate asset upload failed", error);
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("certificateOnboarding:upload_failed"),
        });
      } finally {
        setUploadingSlots((prev) => ({ ...prev, [slotKey]: false }));
      }
    },
    [setAssets, setUploadingSlots, t],
  );

  const handleFileSelect = (slotKey) => (file, validationError) => {
    if (validationError === "invalid_type") {
      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t("certificateOnboarding:invalid_file_type"),
      });
      return;
    }
    if (validationError === "size") {
      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t("certificateOnboarding:svg_file_size_limit"),
      });
      return;
    }
    if (file) {
      void uploadFile(slotKey, file);
    }
  };

  const handleRemove = (slotKey) => () => {
    setAssets((prev) => ({ ...prev, [slotKey]: null }));
  };

  const slots = [
    {
      id: "cert-onboarding-preview-svg",
      stepNumber: "01",
      gridClass: "",
      slotKey: SLOT_KEYS.previewSvg,
      label: t("certificateOnboarding:preview_svg_label"),
      helperText: t("certificateOnboarding:preview_svg_helper"),
      formatHint: t("certificateOnboarding:svg_format_hint"),
      acceptedExtensions: ["svg"],
      previewType: "svg",
    },
    {
      id: "cert-onboarding-template-svg",
      stepNumber: "02",
      gridClass: "",
      slotKey: SLOT_KEYS.templateSvg,
      label: t("certificateOnboarding:template_svg_label"),
      helperText: t("certificateOnboarding:template_svg_helper"),
      formatHint: t("certificateOnboarding:svg_format_hint"),
      acceptedExtensions: ["svg"],
      previewType: "svg",
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {slots.map((slot, index) => {
        const isPreviousSlotsMissing = slots
          .slice(0, index)
          .some((prev) => !assets[prev.slotKey]);
        const isDisabled = !isEntityNameValidated || isPreviousSlotsMissing;

        return (
          <div key={slot.slotKey} className={slot.gridClass}>
            <CertificateAssetUploadSlot
              id={slot.id}
              stepNumber={slot.stepNumber}
              label={slot.label}
              helperText={slot.helperText}
              formatHint={slot.formatHint}
              acceptedExtensions={slot.acceptedExtensions}
              previewType={slot.previewType}
              uploadedAsset={assets[slot.slotKey]}
              isUploading={Boolean(uploadingSlots[slot.slotKey])}
              onFileSelect={handleFileSelect(slot.slotKey)}
              onRemove={handleRemove(slot.slotKey)}
              disabled={isDisabled}
              required
              t={t}
            />
          </div>
        );
      })}
    </div>
  );
}

export { SLOT_KEYS };
export default CertificateUploadStep;
